// Background colours for the mask shapes come from the CMS (a free colour
// picker) and from the per-page accent, so neither black nor white text is
// legible across the whole range. Pick whichever of the two has the better
// WCAG contrast ratio against the background.

const HEX = /^#(?:[0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i
const RGB = /^rgba?\(([^)]+)\)$/i
const VAR_REF = /^var\(\s*(--[\w-]+)\s*(?:,([\s\S]+))?\)$/

const parseChannel = (value: string): number | null => {
  const trimmed = value.trim()
  if (!trimmed) return null
  const numeric = Number.parseFloat(trimmed)
  if (Number.isNaN(numeric)) return null
  return trimmed.endsWith('%') ? (numeric / 100) * 255 : numeric
}

// Returns 0–255 channels, or null for anything we can't resolve statically —
// `var(--x)`, `oklch()`, named colours. Callers fall back rather than guess.
const parseRgb = (color: string): [number, number, number] | null => {
  const value = color.trim()

  if (HEX.test(value)) {
    const hex = value.slice(1)
    // #rgb / #rgba shorthand doubles each digit; #rrggbb / #rrggbbaa is already
    // pairwise. Either way the alpha pair at the end is ignored.
    const full = hex.length <= 4 ? hex.split('').map(c => c + c).join('') : hex
    return [0, 2, 4].map(i => Number.parseInt(full.slice(i, i + 2), 16)) as [number, number, number]
  }

  const rgb = value.match(RGB)
  if (rgb) {
    const channels = rgb[1]!.split(/[\s,/]+/).filter(Boolean).slice(0, 3).map(parseChannel)
    if (channels.length === 3 && channels.every(c => c !== null)) {
      return channels as [number, number, number]
    }
  }

  return null
}

const srgbToLinear = (channel: number) => {
  const c = channel / 255
  return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
}

export const relativeLuminance = ([r, g, b]: [number, number, number]) =>
  0.2126 * srgbToLinear(r) + 0.7152 * srgbToLinear(g) + 0.0722 * srgbToLinear(b)

interface ContrastOptions {
  light?: string
  dark?: string
  /** Returned when `color` can't be resolved to an rgb triplet. */
  fallback?: string | null
}

export const contrastTextColor = (
  color: string | null | undefined,
  { light = 'var(--color--white, #ffffff)', dark = 'var(--color--black, #000000)', fallback = null }: ContrastOptions = {},
): string | null => {
  // A palette reference can't be measured here, but every entry publishes its
  // own `--color--x-contrast` (assets/css/variables/color.css, and
  // page/header.vue for the accent), so hand the job to the browser. The var's
  // own fallback, if it has one, becomes the contrast var's fallback.
  const varRef = color?.trim().match(VAR_REF)
  if (varRef) {
    const inner = contrastTextColor(varRef[2], { light, dark })
    return `var(${varRef[1]}-contrast${inner ? `, ${inner}` : ''})`
  }

  const rgb = color ? parseRgb(color) : null
  if (!rgb) return fallback

  // Contrast ratio against white is (1 + 0.05) / (L + 0.05), against black it
  // is (L + 0.05) / 0.05; they cross at L = sqrt(0.0525) - 0.05.
  return relativeLuminance(rgb) > Math.sqrt(0.0525) - 0.05 ? dark : light
}
